import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useAction } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the Convex npm package, not the local convex/ directory
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up
import { useOrgContext } from '@/hooks/useOrgContext';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { PROVIDERS_COLUMNS } from '@/lib/tableColumns';
import { Building2, Plus, CheckCircle, Loader2 } from 'lucide-react';
import type { IInsuranceProvider } from '@/@types/insurance';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function ProvidersListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [deactivateProvider, setDeactivateProvider] = useState<IInsuranceProvider | null>(null);
  const toggleActive = useMutation(api['insurance/providers'].toggleActive);
  const manualScrape = useAction(api['insurance/portalScraper'].manualScrape);
  const [scrapingProviderId, setScrapingProviderId] = useState<string | null>(null); // LOCAL_STATE_JUSTIFIED: scraping loading state is tightly coupled to the Scrape Now button

  const rawProviders = useQuery(
    api['insurance/providers'].list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const configuredSlugs = useQuery(
    api['insurance/portalCredentials'].listConfiguredSlugs,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const configuredSlugsSet = useMemo(() => new Set(configuredSlugs ?? []), [configuredSlugs]);

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-providers', PROVIDERS_COLUMNS);

  const isLoading = orgLoading || rawProviders === undefined;
  const providers = useMemo(() => rawProviders ?? [], [rawProviders]);

  const filteredProviders = useMemo(() => {
    if (!searchQuery) return providers;
    const query = searchQuery.toLowerCase();
    return providers.filter(p => p.name.toLowerCase().includes(query));
  }, [providers, searchQuery]);

  const providerAccessor = useCallback(
    (provider: IInsuranceProvider, colId: string): string | number => {
      switch (colId) {
        case 'name':
          return provider.name;
        case 'website':
          return provider.website || '';
        case 'contacts':
          return provider.contacts.length;
        case 'isActive':
          return provider.isActive !== false ? 1 : 0;
        default:
          return '';
      }
    },
    []
  );
  const {
    sortState,
    toggleSort,
    sortedItems: sortedProviders,
  } = useTableSort(
    filteredProviders,
    PROVIDERS_COLUMNS,
    { defaultSort: { sortBy: 'name', direction: 'asc' } },
    providerAccessor
  );

  const handleToggleActive = useCallback(
    (provider: IInsuranceProvider, checked: boolean) => {
      if (!checked) {
        // Deactivating — show confirmation dialog
        setDeactivateProvider(provider);
      } else {
        // Activating — no confirmation needed
        void toggleActive({ providerId: provider._id, isActive: true });
      }
    },
    [toggleActive]
  );

  const handleConfirmDeactivate = useCallback(() => {
    if (deactivateProvider) {
      void toggleActive({ providerId: deactivateProvider._id, isActive: false });
      setDeactivateProvider(null);
    }
  }, [deactivateProvider, toggleActive]);

  const handleScrape = useCallback(
    async (providerId: string, providerName: string) => {
      if (!currentOrg || scrapingProviderId) return;
      setScrapingProviderId(providerId);
      try {
        const providerSlug = providerName.toLowerCase().replace(/\s+/g, '-');
        await manualScrape({
          orgId: currentOrg._id,
          providerSlug,
        });
      } catch (err) {
        console.error('Scrape failed:', err);
      } finally {
        setScrapingProviderId(null);
      }
    },
    [currentOrg, scrapingProviderId, manualScrape]
  );

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage insurance providers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Providers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${providers.length} total`}
          </p>
        </div>
        <Button onClick={() => navigate('/insurance/providers/new')}>
          <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
          New Provider
        </Button>
      </div>

      {/* Table Container */}
      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        {/* Search + Column Settings */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="provider-search" className="sr-only">
            Search providers
          </label>
          <Input
            id="provider-search"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {/* Table Header */}
          <div className="flex items-center gap-4 px-6 py-2 border-b border-table-row-border bg-muted/30 min-w-max">
            {visibleColumns.map(col => (
              <div
                key={col.id}
                className={cn(
                  'shrink-0',
                  col.width ?? 'flex-1',
                  col.pinned === 'left' && 'sticky left-0 z-10 bg-muted/30',
                  !col.sortType && 'text-center'
                )}
              >
                <SortableColumnHeader
                  column={col}
                  sortState={sortState}
                  onToggleSort={toggleSort}
                />
              </div>
            ))}
          </div>

          {/* Table Body */}
          <div>
            {isLoading && (
              <div className="flex items-center justify-center py-16">
                <p className="text-sm text-muted-foreground">Loading providers...</p>
              </div>
            )}

            {!isLoading &&
              sortedProviders.map(provider => (
                <div
                  key={provider._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/insurance/providers/${provider._id}`)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/insurance/providers/${provider._id}`);
                    }
                  }}
                  className="flex items-center gap-4 px-6 py-3 w-full text-left border-b border-table-row-border hover:bg-muted/20 transition-colors min-w-max cursor-pointer"
                >
                  {visibleColumns.map(col => {
                    switch (col.id) {
                      case 'name':
                        return (
                          <span
                            key={col.id}
                            className={cn(
                              'shrink-0 flex-1 text-sm font-medium text-foreground truncate',
                              'sticky left-0 z-10 bg-table-container'
                            )}
                          >
                            {provider.name}
                          </span>
                        );
                      case 'isActive':
                        return (
                          <span
                            key={col.id}
                            className="shrink-0 w-20 flex items-center justify-center"
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                          >
                            <Switch
                              checked={provider.isActive !== false}
                              onCheckedChange={checked => handleToggleActive(provider, checked)}
                            />
                          </span>
                        );
                      case 'website':
                        return (
                          <span
                            key={col.id}
                            className="shrink-0 flex-1 text-sm text-muted-foreground truncate"
                          >
                            {provider.website || '—'}
                          </span>
                        );
                      case 'contacts':
                        return (
                          <span
                            key={col.id}
                            className="shrink-0 flex-1 text-sm text-muted-foreground"
                          >
                            {provider.contacts.length} contact
                            {provider.contacts.length !== 1 ? 's' : ''}
                          </span>
                        );
                      case 'portal':
                        return (
                          <span
                            key={col.id}
                            className="shrink-0 w-24 flex items-center justify-center"
                          >
                            {configuredSlugsSet.has(
                              provider.name.toLowerCase().replace(/\s+/g, '-')
                            ) && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </span>
                        );
                      case 'lastScraped':
                        return (
                          <span
                            key={col.id}
                            className="shrink-0 w-36 flex items-center gap-2"
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                          >
                            {configuredSlugsSet.has(
                              provider.name.toLowerCase().replace(/\s+/g, '-')
                            ) && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={scrapingProviderId === provider._id}
                                  onClick={e => {
                                    e.stopPropagation();
                                    void handleScrape(provider._id, provider.name);
                                  }}
                                >
                                  {scrapingProviderId === provider._id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    'Scrape'
                                  )}
                                </Button>
                              </>
                            )}
                          </span>
                        );
                      default:
                        return <span key={col.id} className="shrink-0 flex-1" />;
                    }
                  })}
                </div>
              ))}

            {!isLoading && filteredProviders.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-1">No providers found</h3>
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? 'Try adjusting your search query'
                    : 'Create your first provider to get started'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deactivation Confirmation Dialog */}
      <AlertDialog
        open={deactivateProvider !== null}
        onOpenChange={open => {
          if (!open) setDeactivateProvider(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Provider?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing active policies with{' '}
              <span className="font-medium text-foreground">{deactivateProvider?.name}</span> will
              remain active until they expire. However, no new policies can be created and existing
              policies cannot be renewed with this provider.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeactivate}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
